import { useState } from 'react';
import { getPingStatus } from '../utils/legend';

// Not final - more toggles (tollgates, etc.) get added here once decided.
const LAYER_DEFAULTS = { geofence: false };

// Nested child keys are namespaced so every section can share one onToggle
// handler without colliding.
const ROUTE_KEY_PREFIX = 'route:';
const ALL_ROUTES_KEY = `${ROUTE_KEY_PREFIX}all`;
const PING_KEY_PREFIX = 'ping:';
const ALL_PINGS_KEY = `${PING_KEY_PREFIX}all`;
const MEASURE_KEY = 'measure';

const capitalize = (value) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : value);

// Flattens a truck_legend/ping_legend style array (type -> sub_types) into
// swatch rows for the read-only map key.
const buildStatusLegendGroup = (title, legend = []) => ({
  title,
  items: legend
    .filter((entry) => entry.status)
    .map((entry) => ({
      label: entry.status,
      color: entry.code,
      subItems: (entry.sub_types || []).map((sub) => ({ label: sub.status, color: sub.code })),
    })),
});

/**
 * Owns every bit of "what's toggled on" state for the map (which routes,
 * which ping statuses, geofence on/off) and turns that, plus the legend
 * data, into what the UI actually needs: the filtered routes/locations to
 * hand the map, and the layers drawer's config (checkboxes + read-only
 * map key), so index.jsx stays plain orchestration + rendering.
 */
export const useMapLayers = ({
  routes, locations, routeLegend, truckLegend, pingLegend, routePointLegend, isMeasuring, onToggleMeasuring,
}) => {
  const [activeLayers, setActiveLayers] = useState(LAYER_DEFAULTS);
  // Routes can repeat the same data_source (the API can return several
  // "google" variants for one trip), so selection is per array position, not
  // per data_source. Only explicit opt-outs are tracked, so every route is
  // selected by default without needing to seed this once routes load.
  const [routeOverrides, setRouteOverrides] = useState({});
  // Pings are grouped by their resolved status (e.g. "DELAYED"), since
  // that's the meaningful "kind" a viewer would want to filter by.
  const [pingOverrides, setPingOverrides] = useState({});

  const toggleLayer = (key) => {
    setActiveLayers((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const isRouteSelected = (index) => routeOverrides[index] !== false;

  const toggleRoute = (index) => {
    setRouteOverrides((prev) => ({ ...prev, [index]: !isRouteSelected(index) }));
  };

  const allRoutesSelected = routes.length > 0 && routes.every((route, index) => isRouteSelected(index));
  const someRoutesSelected = routes.some((route, index) => isRouteSelected(index));

  const toggleAllRoutes = () => {
    const next = {};
    routes.forEach((route, index) => { next[index] = !allRoutesSelected; });
    setRouteOverrides(next);
  };

  const pingStatusOf = (location) => getPingStatus(location.type, location.sub_type, pingLegend);
  const pingStatusCounts = locations.reduce((counts, location) => {
    const status = pingStatusOf(location);
    counts[status] = (counts[status] || 0) + 1;
    return counts;
  }, {});
  const pingStatuses = Object.keys(pingStatusCounts);

  const isPingSelected = (status) => pingOverrides[status] !== false;

  const togglePing = (status) => {
    setPingOverrides((prev) => ({ ...prev, [status]: !isPingSelected(status) }));
  };

  const allPingsSelected = pingStatuses.length > 0 && pingStatuses.every(isPingSelected);
  const somePingsSelected = pingStatuses.some(isPingSelected);

  const toggleAllPings = () => {
    const next = {};
    pingStatuses.forEach((status) => { next[status] = !allPingsSelected; });
    setPingOverrides(next);
  };

  const handleLayerToggle = (key) => {
    if (key === ALL_ROUTES_KEY) {
      toggleAllRoutes();
    } else if (key.startsWith(ROUTE_KEY_PREFIX)) {
      toggleRoute(Number(key.slice(ROUTE_KEY_PREFIX.length)));
    } else if (key === ALL_PINGS_KEY) {
      toggleAllPings();
    } else if (key.startsWith(PING_KEY_PREFIX)) {
      togglePing(key.slice(PING_KEY_PREFIX.length));
    } else if (key === MEASURE_KEY) {
      onToggleMeasuring();
    } else {
      toggleLayer(key);
    }
  };

  const visibleRoutes = routes.filter((route, index) => isRouteSelected(index));
  const visibleLocations = locations.filter((location) => isPingSelected(pingStatusOf(location)));

  const mapKeyGroups = [
    {
      title: 'Routes',
      items: routeLegend.map((entry) => ({ label: capitalize(entry.data_source), color: entry.color, isLine: true })),
    },
    {
      title: 'Waypoints',
      items: routePointLegend.map((entry) => ({
        label: entry.location === 'source' ? 'Source (S)' : 'Destination (D)',
        color: entry.color,
      })),
    },
    buildStatusLegendGroup('Vehicle Status', truckLegend),
    buildStatusLegendGroup('Ping Status', pingLegend),
  ].filter((group) => group.items.length);

  const layerOptions = [
    { key: 'geofence', label: 'Geofence', checked: activeLayers.geofence },
    { key: MEASURE_KEY, label: 'Measure Distance', checked: !!isMeasuring },
    ...(routes.length ? [{
      key: 'routes',
      label: 'Routes',
      children: [
        {
          key: ALL_ROUTES_KEY,
          label: 'All Routes',
          checked: allRoutesSelected,
          indeterminate: someRoutesSelected && !allRoutesSelected,
          isAll: true,
        },
        ...routes.map((route, index) => ({
          key: `${ROUTE_KEY_PREFIX}${index}`,
          label: `Route ${index + 1}`,
          checked: isRouteSelected(index),
        })),
      ],
    }] : []),
    ...(pingStatuses.length ? [{
      key: 'pings',
      label: 'Pings',
      children: [
        {
          key: ALL_PINGS_KEY,
          label: `All Pings (${locations.length})`,
          checked: allPingsSelected,
          indeterminate: somePingsSelected && !allPingsSelected,
          isAll: true,
        },
        ...pingStatuses.map((status) => ({
          key: `${PING_KEY_PREFIX}${status}`,
          label: `${status} (${pingStatusCounts[status]})`,
          checked: isPingSelected(status),
        })),
      ],
    }] : []),
    ...(mapKeyGroups.length ? [{ key: 'mapKey', label: 'Map Key', legend: true, groups: mapKeyGroups }] : []),
  ];

  return {
    layerOptions,
    handleLayerToggle,
    visibleRoutes,
    visibleLocations,
    showGeofence: activeLayers.geofence,
  };
};
